import {
  currentShipmentValue,
  type CurrentShipmentInput,
} from "@/api/shipment-current";
import { useState } from "react";
import {
  historyErrorLabel,
  useShipmentHistory,
  useShipmentHistoryRevision,
  type HistoryValue,
  type ShipmentHistoryRevision,
} from "@/api/shipment-history";
import { Button } from "@/components/ui/button";
import type { ReactNode } from "react";

const labels: Record<string, string> = {
  title: "Shipment definition history",
  intro:
    "Recorded applied definitions. Pending or refused requests are separate; history does not confirm physical work or payment completion.",
  open: "View history",
  close: "Close history",
  refresh: "Refresh history",
  older: "Load older history",
  loading: "Loading history…",
  empty: "No recorded history. Earlier values are unknown.",
  denied: "History is not available for this account or Shipment.",
  unavailable:
    "History is temporarily unavailable. Current details remain available.",
  error: "History could not be loaded. Try again.",
  retry: "Try again",
  before: "Before",
  after: "Recorded after",
  full: "Complete recorded definition",
  changes: "Changed values",
  clear: "Not set / explicitly cleared",
  none: "None",
  yes: "Yes",
  no: "No",
  AVAILABLE: "Available",
  REDACTED: "Redacted under retention policy",
  UNSUPPORTED_SCHEMA: "Recorded version cannot be displayed",
  INVALID_RECORDED_DEFINITION: "Recorded definition unavailable",
  UNAVAILABLE: "Value unavailable",
  NONE: "No recorded predecessor",
  CREATED: "Created",
  APPLIED_CHANGE: "Applied change",
  OBSERVED_BASELINE: "Observed baseline — earlier history unknown",
  UNKNOWN_LEGACY: "Unknown historical value",
  CAPTURED: "Recorded",
  RETAINED_DISPLAY: "Retained display",
  NOT_APPLICABLE: "Not applicable",
  actor: "Applied by role",
  policy: "Authorizing policy",
  request: "Related request",
  review: "Related review",
  operation: "Related operation",
  unknown: "Unknown",
  notRecorded: "Not recorded",
  adjustmentScope: "Adjustment scope",
  orderWide: "Order-wide adjustment",
  orderWideNote:
    "Applies once to the Order. No per-Shipment allocation is established; the amount is not an additional charge for each Shipment.",
  notLoaded: "Previous value context not loaded — expand complete definition",
  noDiff: "Comparison unavailable; do not infer unchanged values.",
  unchanged: "No audience-visible differences.",
  recorded: "Recorded at",
  revision: "Revision",
  ADMIN: "Admin",
  CUSTOMER: "Customer",
  CARRIER: "Carrier",
  OPERATOR: "Operator",
  SYSTEM: "System",
  UNKNOWN: "Unknown",
};
function fieldLabel(key: string) {
  return key
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .replace(/_/g, " ")
    .replace(/^./, (v) => v.toUpperCase());
}
function RecordedValue({
  value,
  field,
  unknown = false,
  nullLabel,
}: {
  value: HistoryValue;
  field?: string;
  unknown?: boolean;
  nullLabel?: string;
}) {
  const t = (key: string) => labels[key] ?? key;
  if (value === null)
    return (
      <span className="italic text-muted-foreground">
        {t(nullLabel ?? (unknown ? "UNKNOWN_LEGACY" : "clear"))}
      </span>
    );
  if (typeof value === "boolean") return <span>{t(value ? "yes" : "no")}</span>;
  if (typeof value !== "object")
    return (
      <span className="whitespace-pre-wrap break-words">
        {field === "handlingAvailability" && value === "UNKNOWN_LEGACY"
          ? t("notRecorded")
          : (field === "availability" || field === "handlingAvailability") &&
              typeof value === "string" &&
              value in labels
            ? t(value)
            : String(value)}
      </span>
    );
  if (
    !Array.isArray(value) &&
    value.value === null &&
    ["UNKNOWN_LEGACY", "UNAVAILABLE", "NOT_APPLICABLE", "REDACTED"].includes(
      String(value.availability)
    )
  ) {
    return (
      <span className="text-muted-foreground">
        {t(String(value.availability))}
      </span>
    );
  }
  if (Array.isArray(value))
    return value.length ? (
      <ol className="space-y-3">
        {value.map((item, index) => (
          <li
            key={JSON.stringify([index, item])}
            className="rounded border p-2"
          >
            <RecordedValue
              value={item}
              field={field}
              unknown={unknown}
              nullLabel={nullLabel}
            />
          </li>
        ))}
      </ol>
    ) : (
      <span>{t("none")}</span>
    );
  return (
    <dl className="min-w-0 space-y-2">
      {Object.entries(value).map(([key, item]) => (
        <div key={key} className="min-w-0 border-l pl-3">
          <dt className="text-xs font-medium text-muted-foreground">
            {field === "adjustments" && key === "shipmentId"
              ? t("adjustmentScope")
              : fieldLabel(key)}
          </dt>
          <dd className="min-w-0">
            {field === "adjustments" &&
            key === "shipmentId" &&
            item === null ? (
              <>
                <p className="font-medium">{t("orderWide")}</p>
                <p className="text-muted-foreground">{t("orderWideNote")}</p>
              </>
            ) : (
              <RecordedValue
                value={item}
                field={key}
                nullLabel={
                  (field === "adjustments" && key === "taxCents") ||
                  (key === "handlingNotes" &&
                    value.handlingAvailability === "UNKNOWN_LEGACY")
                    ? "notRecorded"
                    : key === "handlingNotes" &&
                        value.handlingAvailability === "CAPTURED"
                      ? "clear"
                      : nullLabel
                }
                unknown={
                  unknown ||
                  value.availability === "UNKNOWN_LEGACY" ||
                  value.availability === "UNAVAILABLE"
                }
              />
            )}
          </dd>
        </div>
      ))}
    </dl>
  );
}
function unknownAtPath(value: HistoryValue | undefined, path: string): boolean {
  let current = value;
  for (const key of path
    .split("/")
    .slice(1)
    .map((part) => part.replace(/~1/g, "/").replace(/~0/g, "~"))) {
    if (
      current === undefined ||
      current === null ||
      typeof current !== "object" ||
      Array.isArray(current)
    )
      return true;
    if (current.availability === "UNKNOWN_LEGACY") return true;
    current = current[key];
  }
  return current === undefined;
}
function Revision({ item }: { item: ShipmentHistoryRevision }) {
  const t = (key: string) => labels[key] ?? key;
  const [expanded, setExpanded] = useState(false);
  const previous = useShipmentHistoryRevision(
    item.shipmentId,
    item.previousRevisionId,
    expanded
  );
  const before = previous.data?.items[0];
  return (
    <article className="min-w-0 space-y-3 rounded-lg border p-3">
      <h4 className="font-medium">
        {t(item.kind)} · {t("revision")} {item.sequence}
      </h4>
      <p>
        {t("recorded")}:{" "}
        <time dateTime={item.recordedAt}>
          {new Date(item.recordedAt).toLocaleString()}
        </time>
      </p>
      <p>
        {t("actor")}: {t(item.actor.role)}
      </p>
      {item.cause.authorizingPolicy && (
        <p className="break-words">
          {t("policy")}: {item.cause.authorizingPolicy}
        </p>
      )}
      {item.cause.requestId && (
        <p className="break-all">
          {t("request")}: {item.cause.requestId}
        </p>
      )}
      {item.cause.reviewId && (
        <p className="break-all">
          {t("review")}: {item.cause.reviewId}
        </p>
      )}
      {item.cause.operationId && (
        <p className="break-all">
          {t("operation")}: {item.cause.operationId}
        </p>
      )}
      {item.definition.availability !== "AVAILABLE" && (
        <p role="status">{t(item.definition.availability)}</p>
      )}
      <h5 className="font-medium">{t("changes")}</h5>
      {item.diff.availability !== "AVAILABLE" ? (
        <p>
          {t("noDiff")} {t(item.predecessorAvailability)}
        </p>
      ) : item.diff.fields?.length ? (
        item.diff.fields.map((field) => (
          <section key={field.path} className="min-w-0 rounded bg-muted/40 p-2">
            <h6 className="break-words font-medium">
              {field.path
                .split("/")
                .filter(Boolean)
                .map(fieldLabel)
                .join(" · ")}
            </h6>
            <div className="grid min-w-0 gap-3 sm:grid-cols-2">
              <div className="min-w-0">
                <p className="font-medium">{t("before")}</p>
                <RecordedValue
                  value={field.before}
                  field={field.path.split("/").slice(-1)[0]}
                  nullLabel={!before ? "notLoaded" : undefined}
                  unknown={unknownAtPath(
                    before?.definition.value ?? undefined,
                    field.path
                  )}
                />
              </div>
              <div className="min-w-0">
                <p className="font-medium">{t("after")}</p>
                <RecordedValue
                  value={field.after}
                  field={field.path.split("/").slice(-1)[0]}
                  unknown={unknownAtPath(
                    item.definition.value ?? undefined,
                    field.path
                  )}
                />
              </div>
            </div>
          </section>
        ))
      ) : (
        <p>{t("unchanged")}</p>
      )}
      <details onToggle={(event) => setExpanded(event.currentTarget.open)}>
        <summary className="min-h-11 cursor-pointer py-3 font-medium">
          {t("full")}
        </summary>
        {expanded && (
          <div className="grid min-w-0 gap-4 sm:grid-cols-2">
            <section className="min-w-0">
              <h5 className="mb-2 font-medium">{t("before")}</h5>
              {!item.previousRevisionId ||
              item.predecessorAvailability !== "AVAILABLE" ? (
                <p>{t(item.predecessorAvailability)}</p>
              ) : previous.isError ? (
                <div role="alert">
                  <p>{t(historyErrorLabel(previous.error))}</p>
                  <Action onClick={() => void previous.refetch()}>
                    {t("retry")}
                  </Action>
                </div>
              ) : previous.isPending ? (
                <p role="status">{t("loading")}</p>
              ) : before?.definition.availability === "AVAILABLE" &&
                before.definition.value ? (
                <RecordedValue value={before.definition.value} />
              ) : (
                <p>{t(before?.definition.availability ?? "UNAVAILABLE")}</p>
              )}
            </section>
            <section className="min-w-0">
              <h5 className="mb-2 font-medium">{t("after")}</h5>
              {item.definition.availability === "AVAILABLE" &&
              item.definition.value ? (
                <RecordedValue value={item.definition.value} />
              ) : (
                <p>{t(item.definition.availability)}</p>
              )}
            </section>
          </div>
        )}
      </details>
    </article>
  );
}
export function ShipmentRevisionHistory({
  shipmentId,
  updatedAt,
}: {
  shipmentId: string;
  updatedAt?: string;
}) {
  const t = (key: string) => labels[key] ?? key;
  const [open, setOpen] = useState(false);
  return (
    <section className="min-w-0 space-y-3 text-sm" aria-label={t("title")}>
      <Action onClick={() => setOpen(!open)} expanded={open}>
        {t(open ? "close" : "open")}
      </Action>
      {open && (
        <HistoryContent
          key={`${shipmentId}:${updatedAt ?? ""}`}
          shipmentId={shipmentId}
          updatedAt={updatedAt}
        />
      )}
    </section>
  );
}
function HistoryContent({
  shipmentId,
  updatedAt,
}: {
  shipmentId: string;
  updatedAt?: string;
}) {
  const t = (key: string) => labels[key] ?? key;
  const history = useShipmentHistory(shipmentId, true, updatedAt);
  return (
    <>
      <h3 className="font-semibold">{t("title")}</h3>
      <p className="text-muted-foreground">{t("intro")}</p>
      <Action
        onClick={() => void history.refetch()}
        disabled={history.isFetching}
      >
        {t("refresh")}
      </Action>
      {history.isPending && <p role="status">{t("loading")}</p>}
      {history.isError ? (
        <div role="alert">
          <p>{t(historyErrorLabel(history.error))}</p>
          <Action onClick={() => void history.refetch()}>{t("retry")}</Action>
        </div>
      ) : (
        <>
          {history.data?.pages
            .flatMap((page) => page.items)
            .map((item) => (
              <Revision key={item.id} item={item} />
            ))}
          {history.data && history.data.pages[0].items.length === 0 && (
            <p>{t("empty")}</p>
          )}
          {history.hasNextPage && (
            <Action
              onClick={() => void history.fetchNextPage()}
              disabled={history.isFetching}
            >
              {t("older")}
            </Action>
          )}
        </>
      )}
    </>
  );
}

function Action({
  children,
  onClick,
  disabled,
  expanded,
}: {
  children: ReactNode;
  onClick: () => void;
  disabled?: boolean;
  expanded?: boolean;
}) {
  return (
    <Button
      type="button"
      variant="outline"
      onClick={onClick}
      disabled={disabled}
      aria-expanded={expanded}
      className="min-h-11"
    >
      {children}
    </Button>
  );
}

export function CurrentShipmentValues({
  shipment,
}: {
  shipment: CurrentShipmentInput;
}) {
  const [expanded, setExpanded] = useState(false);
  return (
    <details
      onToggle={(event) => setExpanded(event.currentTarget.open)}
      className="min-w-0 rounded-lg border p-3 text-sm"
    >
      <summary className="min-h-11 cursor-pointer py-3 font-semibold">
        Current applied details
      </summary>
      {expanded && <RecordedValue value={currentShipmentValue(shipment)} />}
    </details>
  );
}
